// Fractional indexing for `sort_key` (sync-design §Merge rules). A port of the well-known
// algorithm by David Greenspan / rocicorp ("fractional-indexing", CC0): keys are base-62 strings
// with a variable-length integer part ("a0", "a1", "Zz") and an optional fraction ("a0V").
// The Swift port in PFCore must produce identical keys (shared vectors: fracindex.*).

export const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const ZERO = '0';
const SMALLEST_INTEGER = `A${ZERO.repeat(26)}`;

function digit(c: string | undefined): number {
  const i = c === undefined ? -1 : DIGITS.indexOf(c);
  if (i < 0) throw new Error(`invalid digit: ${c}`);
  return i;
}

function midpoint(a: string, b: string | null): string {
  if (b !== null && a >= b) throw new Error(`${a} >= ${b}`);
  if (a.endsWith(ZERO) || b?.endsWith(ZERO)) throw new Error('trailing zero');
  if (b !== null) {
    // Strip the common prefix, padding `a` with zeros as we go.
    let n = 0;
    while ((a[n] ?? ZERO) === b[n]) n++;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }
  const digitA = a ? digit(a[0]) : 0;
  const digitB = b !== null ? digit(b[0]) : DIGITS.length;
  if (digitB - digitA > 1) return DIGITS[Math.round(0.5 * (digitA + digitB))] as string;
  // The first digits are consecutive.
  if (b !== null && b.length > 1) return b.slice(0, 1);
  return (DIGITS[digitA] as string) + midpoint(a.slice(1), null);
}

function integerLength(head: string): number {
  if (head >= 'a' && head <= 'z') return head.charCodeAt(0) - 97 + 2;
  if (head >= 'A' && head <= 'Z') return 90 - head.charCodeAt(0) + 2;
  throw new Error(`invalid order key head: ${head}`);
}

function integerPart(key: string): string {
  const length = integerLength(key[0] ?? '');
  if (length > key.length) throw new Error(`invalid order key: ${key}`);
  return key.slice(0, length);
}

export function isValidKey(key: string): boolean {
  try {
    validateKey(key);
    return true;
  } catch {
    return false;
  }
}

function validateKey(key: string): void {
  if (key === SMALLEST_INTEGER) throw new Error(`invalid order key: ${key}`);
  const i = integerPart(key);
  const fraction = key.slice(i.length);
  for (const c of key.slice(1)) digit(c);
  if (fraction.endsWith(ZERO)) throw new Error(`invalid order key: ${key}`);
}

function incrementInteger(x: string): string | null {
  const [head, ...digits] = x.split('') as [string, ...string[]];
  let carry = true;
  for (let i = digits.length - 1; carry && i >= 0; i--) {
    const d = digit(digits[i]) + 1;
    if (d === DIGITS.length) digits[i] = ZERO;
    else {
      digits[i] = DIGITS[d] as string;
      carry = false;
    }
  }
  if (!carry) return head + digits.join('');
  if (head === 'Z') return `a${ZERO}`;
  if (head === 'z') return null;
  const h = String.fromCharCode(head.charCodeAt(0) + 1);
  if (h > 'a') digits.push(ZERO);
  else digits.pop();
  return h + digits.join('');
}

function decrementInteger(x: string): string | null {
  const [head, ...digits] = x.split('') as [string, ...string[]];
  const last = DIGITS[DIGITS.length - 1] as string;
  let borrow = true;
  for (let i = digits.length - 1; borrow && i >= 0; i--) {
    const d = digit(digits[i]) - 1;
    if (d === -1) digits[i] = last;
    else {
      digits[i] = DIGITS[d] as string;
      borrow = false;
    }
  }
  if (!borrow) return head + digits.join('');
  if (head === 'a') return `Z${last}`;
  if (head === 'A') return null;
  const h = String.fromCharCode(head.charCodeAt(0) - 1);
  if (h < 'Z') digits.push(last);
  else digits.pop();
  return h + digits.join('');
}

/** A key strictly between `a` and `b` (either may be null: start / end of the list). */
export function keyBetween(a: string | null, b: string | null): string {
  if (a !== null) validateKey(a);
  if (b !== null) validateKey(b);
  if (a !== null && b !== null && a >= b) throw new Error(`${a} >= ${b}`);
  if (a === null) {
    if (b === null) return `a${ZERO}`;
    const ib = integerPart(b);
    const fb = b.slice(ib.length);
    if (ib === SMALLEST_INTEGER) return ib + midpoint('', fb);
    if (ib < b) return ib;
    const res = decrementInteger(ib);
    if (res === null) throw new Error('cannot decrement any more');
    return res;
  }
  if (b === null) {
    const ia = integerPart(a);
    const i = incrementInteger(ia);
    return i === null ? ia + midpoint(a.slice(ia.length), null) : i;
  }
  const ia = integerPart(a);
  const fa = a.slice(ia.length);
  const ib = integerPart(b);
  const fb = b.slice(ib.length);
  if (ia === ib) return ia + midpoint(fa, fb);
  const i = incrementInteger(ia);
  if (i === null) throw new Error('cannot increment any more');
  if (i < b) return i;
  return ia + midpoint(fa, null);
}

/** `n` evenly spread keys between `a` and `b`, in order. */
export function keysBetween(a: string | null, b: string | null, n: number): string[] {
  if (n === 0) return [];
  if (n === 1) return [keyBetween(a, b)];
  if (b === null) {
    const out: string[] = [];
    let c = keyBetween(a, b);
    out.push(c);
    for (let i = 1; i < n; i++) {
      c = keyBetween(c, b);
      out.push(c);
    }
    return out;
  }
  if (a === null) {
    const out: string[] = [];
    let c = keyBetween(a, b);
    out.push(c);
    for (let i = 1; i < n; i++) {
      c = keyBetween(a, c);
      out.push(c);
    }
    return out.reverse();
  }
  const mid = Math.floor(n / 2);
  const c = keyBetween(a, b);
  return [...keysBetween(a, c, mid), c, ...keysBetween(c, b, n - mid - 1)];
}

/** Sibling order: by sort key, then by id when two devices produced the same key. */
export function compareOrder(a: { sortKey: string; id: string }, b: { sortKey: string; id: string }): number {
  if (a.sortKey !== b.sortKey) return a.sortKey < b.sortKey ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
